import React, { useEffect, useState } from "react";
import {
    Card,
    Typography,
    Box,
    Stack,
    Paper,
    Grid,
    Divider,
    Button
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Download } from "@mui/icons-material";

import ItemInfo from "./ItemInfo";
import ServiceInfo from "./ServiceInfo";
import PaymentInfo from "./PaymentInfo";
import CustomerInfo from "./CustomerInfo";
import OrderRequestSent from "./delivery/OrderRequestSent";
import AssignDriver from "./delivery/AssignDriver";

import { useParams, useSearchParams } from "react-router-dom";
import { apiRequest } from "../../utils/config/apiRequest";
import { apiUrl } from "../../utils/endPoints/apiUrl";
import BreadCrumb from "../../comman/Breadcrumb";
import { localizeDateTime } from "../../utils/helper/timeHelpers";
import {
    DELIVERY_STATUS,
    LABEL_TYPE,
    ORDER_TYPE_ENUM,
} from "../../utils/Constant";
import { StatusHandler } from "../comman/StatusHandler";
import { getOrderStatus } from "../../utils/helper/appHelpers";
import ItemInfoV2 from "./ItemInfoV2";
import RouteMap from "../comman/RouteMap";
import SoftCopy from "../../pages/vendor-admin/orders/SoftCopy";

const OrderDetails = () => {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams()
    const [orderInfo, setOrderInfo] = useState({});
    const [load, setLoad] = useState(false);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [softcopy, setsoftcopy] = useState(false)

    useEffect(() => {
        fetchOrder();
    }, []);

    const fetchOrder = async () => {
        const { data, status } = await apiRequest({
            endUrl: apiUrl.orders.detailById,
            params: { orderId },
        });
        if (status) setOrderInfo(data);
    };

    const handleDownloadInvoice = async () => {
        setInvoiceLoading(true);
        const { status, isPdf, data } = await apiRequest({
            method: "GET",
            endUrl: apiUrl.orders.downloadInvoice(orderId),
        });

        if (status && isPdf) {
            const url = window.URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "invoice.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
        }
        setInvoiceLoading(false);
    };

    const isRiderReqAccepted = () =>
        orderInfo?.orderReqInfo?.requestSent?.some((item) =>
            [DELIVERY_STATUS.ACCEPTED, DELIVERY_STATUS.DELIVERED].includes(item.status)
        );

    return (
        <>
            {/* 🔷 HEADER (UNCHANGED) */}
            <BreadCrumb
                isBack
                leftContent={
                    <Box>
                        <Stack flexDirection={"column"}>
                            <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
                                <Typography variant="h5">{orderInfo?.orderNo}</Typography>
                                <Typography variant="h5">{orderInfo?.shopInfo?.name}</Typography>
                                <Box sx={{ alignSelf: "flex-start" }}>
                                    {orderInfo.status && (
                                        <StatusHandler
                                            value={orderInfo.status}
                                            params={{ _id: orderInfo?._id }}
                                            endPoint={apiUrl.orders.updateStatus}
                                            method={"PATCH"}
                                            labelType={LABEL_TYPE.ORDER_STATUS}
                                            statusConstant={getOrderStatus(orderInfo.status)}
                                            onChange={fetchOrder}
                                            disabled={orderInfo.status === "COMPLETED"}
                                        />
                                    )}
                                </Box>
                            </Stack>
                            <Typography variant="subtitle2">
                                {localizeDateTime(orderInfo?.createdAt, orderInfo?.timezone)}
                            </Typography>
                        </Stack>
                    </Box>
                }
                action={
                    <Stack flexDirection={"row"} gap={1} alignItems={"center"}>
                        <LoadingButton
                            startIcon={<Download />}
                            loading={invoiceLoading}
                            variant="contained"
                            onClick={handleDownloadInvoice}
                        >
                            Invoice
                        </LoadingButton>
                    </Stack>
                }
            />

            {/* 🔷 MAIN CONTENT */}
            <Grid container spacing={3}>

                {/* LEFT */}
                <Grid item xs={12} md={8}>

                    {/* ORDER ITEMS */}
                    {
                        orderInfo?.orderItems?.length > 0 &&
                        <Card sx={{ p: 3, borderRadius: 3 }}>
                            <Typography variant="h6" mb={2}>
                                Order Items
                            </Typography>


                            <Stack spacing={2} maxHeight={400}>
                                <Box sx={{ overflowY: 'auto' }}>
                                    {
                                        searchParams.get('v') !== '2' ?
                                            orderInfo?.orderType === ORDER_TYPE_ENUM.DOCUMENT_SCAN ?
                                                <ItemInfoV2
                                                    deliveryType={orderInfo?.deliveryType === 'EXPRESS' ?
                                                        'Express' :
                                                        orderInfo?.deliveryType === 'STANDARD' ?
                                                            'Standard' :
                                                            'Self'}
                                                    status={orderInfo?.status} items={orderInfo?.orderItems || []} /> :
                                                orderInfo?.orderItems?.map((item) => <ServiceInfo key={item._id} item={item} />)
                                            :
                                            orderInfo?.orderItems?.map((item) =>
                                                orderInfo?.orderType === ORDER_TYPE_ENUM.DOCUMENT_SCAN ? (
                                                    <ItemInfo key={item._id} item={item} />
                                                ) : (
                                                    <ServiceInfo key={item._id} item={item} />
                                                )
                                            )
                                    }
                                </Box>

                            </Stack>
                        </Card>
                    }

                    {/* PAYMENT */}
                    <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
                        <Typography variant="h6" mb={2}>
                            Payment Summary
                        </Typography>

                        <PaymentInfo orderInfo={orderInfo} />
                    </Paper>

                    {/* Live Location */}
                    {
                        (orderInfo?.vanService || orderInfo?.priceInfo?.onsiteVisitAmount > 0) && <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
                            <Typography variant="h6" mb={2}>
                                Location
                            </Typography>

                            <RouteMap destination={{
                                latitude: orderInfo?.deliveryAddress?.latitude,
                                longitude: orderInfo?.deliveryAddress?.longitude
                            }} />
                        </Paper>
                    }

                    {/* REQUEST STATUS */}
                    {[ORDER_TYPE_ENUM.DOCUMENT_SCAN, ORDER_TYPE_ENUM.PASSPORT_PHOTO_SHOOT]?.includes(orderInfo?.orderType) && (
                        <Box mt={3}>
                            <OrderRequestSent orderReqInfo={orderInfo?.orderReqInfo} />
                        </Box>
                    )}
                </Grid>

                {/* RIGHT */}
                <Grid item xs={12} md={4}>

                    {/* DELIVERY */}
                    {[ORDER_TYPE_ENUM.DOCUMENT_SCAN, ORDER_TYPE_ENUM.PASSPORT_PHOTO_SHOOT]?.includes(orderInfo?.orderType) && orderInfo?.deliveryType !== 'SELF' &&
                        !isRiderReqAccepted() && (
                            <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                                <Typography variant="h6" mb={2}>
                                    Delivery Assignment
                                </Typography>

                                <Stack spacing={2}>
                                    <AssignDriver
                                        orderId={orderInfo?._id}
                                        shopId={orderInfo?.shopId}
                                        businessOwenerId={orderInfo?.shopKeeperId}
                                        OnSelect={fetchOrder}
                                    />

                                    <Divider>OR</Divider>

                                    <LoadingButton
                                        loading={load}
                                        variant="outlined"
                                        onClick={async () => {
                                            setLoad(true);
                                            await apiRequest({
                                                method: "POST",
                                                endUrl: apiUrl.orders.sendBookingReq(orderId),
                                            });
                                            setLoad(false);
                                            fetchOrder();
                                        }}
                                    >
                                        Request Nearby Rider
                                    </LoadingButton>
                                </Stack>
                            </Card>
                        )}

                    {/* CUSTOMER */}
                    <Card sx={{ p: 3, borderRadius: 3 }}>
                        <Typography variant="h6" mb={2}>
                            Customer Info
                        </Typography>

                        <CustomerInfo orderInfo={orderInfo} />
                    </Card>

                    {/* CUSTOMER */}
                    {
                        orderInfo?.softcopyRequired &&

                        <Card sx={{ my: 3, p: 3, borderRadius: 3 }}>
                            <Typography variant="h6" mb={2}>
                                Soft copy
                            </Typography>

                            <Button onClick={() => setsoftcopy(true)}>Send Soft copy</Button>

                            <SoftCopy
                                orderNo={orderInfo?.orderNo}
                                name={orderInfo?.userInfo?.userName}
                                userId={orderInfo?.userInfo?._id}
                                open={softcopy}
                                onClose={() => setsoftcopy(false)}
                                email={orderInfo?.userInfo?.email} />
                        </Card>
                    }
                </Grid>
            </Grid>
        </>
    );
};

export default OrderDetails;